import { Card, cn, focusRing, Icon, MonoLabel, Skeleton } from '@/design-system';
import type { TemplateCostSource } from '@/entities/template';
import { formatCurrency, formatNumber } from '@/shared/lib';
import { ErrorState } from '@/shared/ui';

import { costSourcesCopy } from '../copy';

export interface CostSourcesCardProps {
    sources: TemplateCostSource[] | undefined;
    loading: boolean;
    error: unknown;
    onRetry: () => void;
}

/**
 * Achtergrond: waar de standaardbedragen van de rapporten vandaan komen (`GET
 * /template-cost-sources`), per vragenlijst in getallen.
 *
 * Alleen inzien en alleen voor de eigenaar, die instaat voor bedragen die niet uit
 * eigen hand komen. Standaard ingeklapt: wie net begint, heeft het niet nodig. Wat
 * de vragenlijst niet vastlegt, zoals het prijspeil en de btw, staat er met zoveel
 * woorden bij in plaats van weg te vallen. Importgegevens (bron, oud versienummer,
 * inleesdatum) staan er niet: die zeggen een keuringsbedrijf niets. Zie
 * `docs/nhg.md` §2.
 */
export function CostSourcesCard({ sources, loading, error, onRetry }: CostSourcesCardProps) {
    const copy = costSourcesCopy;

    return (
        <Card padding="lg" as="section" aria-labelledby="cost-sources-title">
            <details className="group">
                <summary
                    className={cn(
                        'flex min-h-[32px] cursor-pointer list-none items-start gap-2 rounded-[4px] [&::-webkit-details-marker]:hidden',
                        focusRing,
                    )}
                >
                    <Icon
                        name="chevronDown"
                        size="sm"
                        tone="muted"
                        className="mt-[1px] -rotate-90 group-open:rotate-0"
                    />
                    <span className="min-w-0">
                        <h2
                            id="cost-sources-title"
                            className="m-0 font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]"
                        >
                            {copy.title}
                        </h2>
                        <span className="mt-1 block text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                            {copy.intro}
                        </span>
                    </span>
                </summary>

                {loading ? (
                    <div className="mt-4" aria-busy="true">
                        <Skeleton width="55%" height={14} label={copy.loading} />
                        <Skeleton height={58} className="mt-3" />
                        <Skeleton width="80%" height={12} className="mt-3" />
                    </div>
                ) : error ? (
                    <div className="mt-3">
                        <ErrorState error={error} title={copy.title} onRetry={onRetry} />
                    </div>
                ) : sources && sources.length > 0 ? (
                    <div className="mt-1">
                        {sources.map((source) => (
                            <CostSource key={source.versionUuid} source={source} />
                        ))}
                    </div>
                ) : (
                    <p className="mt-3 text-[13px] leading-[1.5] text-[var(--text-secondary)]">
                        {copy.empty}
                    </p>
                )}

                <p className="mt-4 text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                    {copy.readOnly}
                </p>
            </details>
        </Card>
    );
}

function count(value: number): string {
    return formatNumber(value);
}

function CostSource({ source }: { source: TemplateCostSource }) {
    const copy = costSourcesCopy;
    const nhgTotal = source.nhg.codesUsed.length + source.nhg.codesUnused.length;
    const range =
        source.lowestAmount === null || source.highestAmount === null
            ? copy.none
            : `${formatCurrency(source.lowestAmount)} – ${formatCurrency(source.highestAmount)}`;
    const stats: ReadonlyArray<{ label: string; value: string; note?: string }> = [
        { label: copy.optionsWithAmounts, value: count(source.optionsWithAmounts) },
        {
            label: copy.unitPrice,
            value: count(source.unitPriceOptions),
            note: copy.unitPriceNote,
        },
        { label: copy.entered, value: count(source.enteredAmountOptions) },
        { label: copy.fixed, value: count(source.fixedAmountOptions) },
        { label: copy.range, value: range },
    ];
    const missing: string[] = [];
    const known: string[] = [];

    if (source.priceLevel === null) {
        missing.push(copy.priceLevelMissing);
    } else {
        known.push(copy.priceLevel(source.priceLevel));
    }

    if (source.vatIncluded === null) {
        missing.push(copy.vatMissing);
    } else {
        known.push(source.vatIncluded ? copy.vatIncluded : copy.vatExcluded);
    }

    return (
        <article
            aria-label={source.templateName}
            className="grid gap-3 border-t border-[var(--border-subtle-2)] pt-[14px] mt-[14px] first:mt-3 first:border-t-0 first:pt-0"
        >
            <header className="grid gap-[3px]">
                <h3 className="m-0 font-[family-name:var(--font-body)] text-[14px] leading-[1.35] font-medium text-[var(--text-primary)]">
                    {source.templateName}
                </h3>
                <p className="m-0 text-[12px] leading-[1.45] text-[var(--text-tertiary-safe)]">
                    {copy.version(source.version)}
                </p>
            </header>

            <dl className="m-0 grid gap-[10px] [grid-template-columns:repeat(auto-fit,minmax(128px,1fr))]">
                {stats.map((stat) => (
                    <div
                        key={stat.label}
                        className="rounded-[7px] bg-[var(--surface-subtle)] px-[10px] py-2"
                    >
                        <dt className="text-[12px] text-[var(--text-tertiary-safe)]">
                            {stat.label}
                        </dt>
                        <dd className="m-0 mt-[2px]">
                            <span className="font-[family-name:var(--font-mono)] text-[14px] font-medium text-[var(--text-primary)]">
                                {stat.value}
                            </span>
                            {stat.note ? (
                                <span className="block text-[11px] leading-[1.4] text-[var(--text-tertiary-safe)]">
                                    {stat.note}
                                </span>
                            ) : null}
                        </dd>
                    </div>
                ))}
            </dl>

            <MonoLabel size={11} variant="value" tone="muted" as="p" className="m-0">
                {copy.byUrgency(
                    count(source.byUrgency.direct),
                    count(source.byUrgency.termijn),
                    count(source.byUrgency.onderhoud),
                )}
            </MonoLabel>

            <dl className="m-0 grid gap-[10px]">
                <div>
                    <dt className="text-[12px] font-medium text-[var(--text-secondary)]">
                        {copy.nhg}
                    </dt>
                    <dd className="m-0 mt-[2px] text-[13px] leading-[1.5] text-[var(--text-reading)]">
                        {copy.nhgUsed(source.nhg.codesUsed.length, nhgTotal)}
                        {source.nhg.optionsWithoutCode > 0 ? (
                            <span className="mt-1 block text-[12px] text-[var(--text-secondary)]">
                                {copy.nhgWithoutCode(
                                    count(source.nhg.optionsWithoutCode),
                                    source.nhg.optionsWithoutCode === 1,
                                )}
                            </span>
                        ) : null}
                    </dd>
                </div>

                {source.costGroups.length > 0 ? (
                    <div>
                        <dt className="text-[12px] font-medium text-[var(--text-secondary)]">
                            {copy.costGroups}
                        </dt>
                        <dd className="m-0 mt-[2px] text-[13px] leading-[1.5] text-[var(--text-reading)]">
                            {source.costGroups
                                .map(
                                    (group) =>
                                        `${group.name ?? copy.costGroupNone} ${count(group.options)}`,
                                )
                                .join(' · ')}
                        </dd>
                    </div>
                ) : null}
            </dl>

            {known.length > 0 ? (
                <p className="m-0 text-[13px] leading-[1.5] text-[var(--text-reading)]">
                    {known.join(' ')}
                </p>
            ) : null}

            {missing.length > 0 ? (
                <div className="rounded-[8px] border border-[var(--warn-border)] bg-[var(--warn-tint)] px-3 py-[10px]">
                    <p className="m-0 text-[13px] leading-[1.5] font-medium text-[var(--warn-text)]">
                        {copy.notRecordedTitle}
                    </p>
                    <ul className="m-0 mt-1 grid list-disc gap-[2px] pl-5 text-[13px] leading-[1.5] text-[var(--warn-text)]">
                        {missing.map((line) => (
                            <li key={line}>{line}</li>
                        ))}
                    </ul>
                </div>
            ) : null}
        </article>
    );
}
